// TarotCard Component - 3D Animated Card with Dramatic Flip Reveal
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';

/**
 * TarotCard - Beautiful 3D animated tarot card with dramatic reveal
 * Features:
 * - 3D flip animation on reveal
 * - Staggered entrance for spread
 * - Glow effect on hover
 * - Reversed card styling
 * - Deep dive indicator
 */

// Card back design - uses theme colors
const CardBack = () => (
    <div
        className="absolute inset-0 rounded-xl flex items-center justify-center backface-hidden"
        style={{
            background: 'linear-gradient(135deg, var(--theme-secondary), var(--theme-primary-light, #7c3aed), var(--theme-secondary))',
            border: '2px solid var(--theme-accent)',
            opacity: 0.9,
        }}
    >
        {/* Decorative pattern */}
        <div className="absolute inset-2 rounded-lg" style={{ border: '1px solid var(--theme-accent)', opacity: 0.3 }} />
        <div className="absolute inset-4 rounded-lg" style={{ border: '1px solid var(--theme-accent)', opacity: 0.15 }} />
        <div className="relative" style={{ color: 'var(--theme-accent)' }}>
            <Sparkles className="w-12 h-12 opacity-60" />
        </div>
        {/* Corner stars */}
        <span className="absolute top-3 left-3 opacity-40" style={{ color: 'var(--theme-accent)' }}>✦</span>
        <span className="absolute top-3 right-3 opacity-40" style={{ color: 'var(--theme-accent)' }}>✦</span>
        <span className="absolute bottom-3 left-3 opacity-40" style={{ color: 'var(--theme-accent)' }}>✦</span>
        <span className="absolute bottom-3 right-3 opacity-40" style={{ color: 'var(--theme-accent)' }}>✦</span>
    </div>
);

// Card front design
const CardFront = ({ name, emoji, orientation, small, hasDeeperInsight }) => {
    const isReversed = orientation === 'reversed';

    const textSizes = small
        ? { emoji: 'text-2xl sm:text-3xl', name: 'text-[9px] sm:text-[10px]' }
        : { emoji: 'text-3xl sm:text-4xl md:text-5xl', name: 'text-[10px] sm:text-xs' };

    return (
        <div
            className={`
                absolute inset-0 rounded-xl
                flex flex-col items-center justify-center
                p-2 text-center
                backface-hidden
                ${isReversed ? 'rotate-180' : ''}
            `}
            style={{
                transform: 'rotateY(180deg)',
                background: 'linear-gradient(135deg, rgba(17,17,35,0.95), var(--theme-glass-bg), rgba(17,17,35,0.95))',
                border: `2px solid ${hasDeeperInsight ? 'var(--theme-success)' : 'var(--theme-button-secondary-border)'}`,
            }}
        >
            {/* Inner glow effect */}
            <div
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{ background: 'linear-gradient(to top, var(--theme-accent-glow), transparent, var(--theme-glow))' }}
            />

            {/* Corner decorations */}
            <div className="absolute top-1 left-1 w-3 h-3 rounded-tl" style={{ borderTop: '2px solid var(--theme-accent)', borderLeft: '2px solid var(--theme-accent)', opacity: 0.4 }} />
            <div className="absolute top-1 right-1 w-3 h-3 rounded-tr" style={{ borderTop: '2px solid var(--theme-accent)', borderRight: '2px solid var(--theme-accent)', opacity: 0.4 }} />
            <div className="absolute bottom-1 left-1 w-3 h-3 rounded-bl" style={{ borderBottom: '2px solid var(--theme-accent)', borderLeft: '2px solid var(--theme-accent)', opacity: 0.4 }} />
            <div className="absolute bottom-1 right-1 w-3 h-3 rounded-br" style={{ borderBottom: '2px solid var(--theme-accent)', borderRight: '2px solid var(--theme-accent)', opacity: 0.4 }} />

            {/* Card Content */}
            <span className={`${textSizes.emoji} ${isReversed ? 'rotate-180' : ''} drop-shadow-lg`}>
                {emoji}
            </span>

            <p className={`${textSizes.name} text-amber-100 font-serif ${isReversed ? 'rotate-180' : ''} mt-1 leading-tight max-w-full px-1`}>
                {name}
            </p>

            {/* Orientation indicator */}
            {!small && (
                <span className={`text-[8px] uppercase tracking-wider ${isReversed ? 'text-rose-400 rotate-180' : 'text-emerald-400'} mt-0.5 opacity-70`}>
                    {orientation}
                </span>
            )}

            {/* Deeper insight indicator */}
            {hasDeeperInsight && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-teal-400 rounded-full shadow-lg shadow-teal-400/50" />
            )}
        </div>
    );
};

export const TarotCard = ({
    name,
    orientation = 'upright',
    emoji,
    isPlaceholder = false,
    isClickable = false,
    hasDeeperInsight = false,
    small = false,
    onClick,
    index = 0,
    isRevealing = false, // New prop for reveal animation
    revealDelay = 0 // Delay before this card reveals
}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [hasRevealed, setHasRevealed] = useState(!isRevealing);

    // Size variants
    const sizeClasses = small
        ? 'w-20 h-28 sm:w-22 sm:h-32 md:w-24 md:h-36'
        : 'w-28 h-40 sm:w-32 sm:h-44 md:w-36 md:h-52';

    // Handle reveal animation
    useEffect(() => {
        if (isRevealing && !hasRevealed) {
            const timer = setTimeout(() => {
                setIsFlipped(true);
                setHasRevealed(true);
            }, revealDelay);
            return () => clearTimeout(timer);
        }
    }, [isRevealing, revealDelay, hasRevealed]);

    // Reset when card changes
    useEffect(() => {
        if (isRevealing) {
            setIsFlipped(false);
            setHasRevealed(false);
        }
    }, [name, isRevealing]);

    const hoverVariants = {
        rest: {
            scale: 1,
            y: 0,
        },
        hover: {
            scale: 1.05,
            y: -8,
            transition: {
                type: "spring",
                damping: 20,
                stiffness: 300
            }
        },
        tap: {
            scale: 0.98
        }
    };

    // Placeholder/Loading card
    if (isPlaceholder) {
        return (
            <motion.div
                className={`${sizeClasses} relative rounded-xl`}
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
            >
                <CardBack />
                <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                        boxShadow: [
                            '0 0 20px rgba(245, 197, 24, 0.2)',
                            '0 0 40px rgba(245, 197, 24, 0.4)',
                            '0 0 20px rgba(245, 197, 24, 0.2)'
                        ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                />
            </motion.div>
        );
    }

    return (
        <motion.div
            className={`${sizeClasses} relative cursor-${isClickable ? 'pointer' : 'default'}`}
            style={{ perspective: '1000px' }}
            initial={{ opacity: 0, y: -50, rotateX: 30 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{
                delay: index * 0.15,
                duration: 0.5,
                type: "spring",
                damping: 20
            }}
            variants={hoverVariants}
            whileHover={isClickable ? "hover" : "rest"}
            whileTap={isClickable ? "tap" : undefined}
            onClick={isClickable ? onClick : undefined}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : -1}
            onKeyDown={isClickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') onClick?.();
            } : undefined}
        >
            {/* 3D Flip Container */}
            <motion.div
                className="relative w-full h-full"
                style={{ transformStyle: 'preserve-3d' }}
                animate={{
                    rotateY: (isRevealing ? isFlipped : true) ? 180 : 0,
                }}
                transition={{
                    duration: 0.8,
                    type: "spring",
                    damping: 20,
                    stiffness: 100
                }}
            >
                {/* Card Back */}
                <CardBack />

                {/* Card Front */}
                <CardFront
                    name={name}
                    emoji={emoji}
                    orientation={orientation}
                    small={small}
                    hasDeeperInsight={hasDeeperInsight}
                />
            </motion.div>

            {/* Glow effect */}
            <motion.div
                className="absolute inset-0 rounded-xl pointer-events-none"
                animate={{
                    boxShadow: isFlipped || !isRevealing ? [
                        '0 0 20px rgba(245, 197, 24, 0.15)',
                        '0 0 30px rgba(245, 197, 24, 0.25)',
                        '0 0 20px rgba(245, 197, 24, 0.15)'
                    ] : '0 0 10px rgba(245, 197, 24, 0.1)'
                }}
                transition={{ duration: 2, repeat: Infinity }}
            />

            {/* Clickable overlay hint */}
            {isClickable && !small && (
                <motion.div
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors rounded-xl"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                >
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        whileHover={{ scale: 1, opacity: 1 }}
                        className="flex flex-col items-center"
                    >
                        <Sparkles className="w-6 h-6 text-amber-300" />
                        <span className="text-amber-200 text-[10px] mt-1">Deeper Insight</span>
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
    );
};

// Card Spread with Reveal Sequence
export const CardSpread = ({
    cards = [],
    onCardClick,
    isLoading = false,
    showPlaceholders = true,
    revealSequence = true // Enable staggered reveal
}) => {
    const placeholderCount = 5;
    const [isRevealing, setIsRevealing] = useState(false);

    // Trigger reveal when cards arrive
    useEffect(() => {
        if (cards.length > 0 && revealSequence) {
            setIsRevealing(true);
        }
    }, [cards, revealSequence]);

    if (isLoading && showPlaceholders) {
        return (
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {Array(placeholderCount).fill(null).map((_, i) => (
                    <TarotCard key={i} isPlaceholder index={i} />
                ))}
            </div>
        );
    }

    return (
        <motion.div
            className="flex flex-wrap justify-center gap-3 sm:gap-4"
            initial="hidden"
            animate="visible"
        >
            {cards.map((card, index) => (
                <TarotCard
                    key={`${card.name}-${index}`}
                    name={card.name}
                    orientation={card.orientation}
                    emoji={card.emoji}
                    isClickable={!!onCardClick}
                    hasDeeperInsight={!!card.deeperInterpretation}
                    onClick={() => onCardClick?.(card)}
                    index={index}
                    isRevealing={revealSequence && isRevealing}
                    revealDelay={index * 600} // 600ms between each card flip
                />
            ))}
        </motion.div>
    );
};

export default TarotCard;
